import {BudgetingType} from "../../../BudgetingType";
import {AppConfig} from "../../../hooks/useConfig";
import {ExportPropertyBudgetQuery, FinancialEntityType, GetBudgetSeasonExportDataQuery, VersionType} from "../../../__generated__/generated_types";
import {YearDefinition} from "../../annualsummary/YearDefinitions";
import {getMonthIndexes, getMonths} from "../../../utils/months-helper";
import {getMonthYears, getYear} from "../export_helpers";
import {Property} from "../../../contexts/properties/logic";
import {buildChartOfAccountsData} from "../../../contexts/chartofaccounts/logic";
import {buildChartOfAccountsFlatComponentsBottomLine} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";


export function formatAsReport(
    {tab, referenceYear, data, config}:
        {
            tab: BudgetingType;
            referenceYear: YearDefinition;
            data: ExportPropertyBudgetQuery;
            config: AppConfig;
        }): string {
    const property = config.properties.currentProperty;
    const isAcquisition = property?.isAcquisitionProperty ?? false;
    const year = getYear(property, config, tab);
    const [months, years] = getMonthYears(property, year);

    const headers = [
        "GL Number",
        "GL Name",
        ...new Array(12).fill(0).map((_, i) => `${months[i]}.${years[i]}`),
        isAcquisition ? year : `${year} ${tab}`,
        isAcquisition ? "T12" : `${referenceYear.year} ${referenceYear.type == VersionType.YearTotal ? "Reforecast" : referenceYear.type == VersionType.Budget ? "Budget" : "Actuals"}`,
        "Note",
        "Variance %",
        "Variance $"
    ];

    const tabularData = [headers.join(",")];


    for (const record of data.getPropertyBudgetExportSingleYear ?? []) {
        const isIncome = config.chartOfAccountsConfig.getIsIncome(record.id);
        const variance$ = (record.yearTotal - record.referenceYearTotal) * (isIncome ? 1 : -1);
        const variancePct = record.referenceYearTotal ? variance$ / Math.abs(record.referenceYearTotal) : NaN;

        const row = [
            `"${record.glNumber}"`,
            `"${record.glName}"`,
            ...record.yearValues.map(val => val.toFixed(2).toString()),
            record.yearTotal.toFixed(2).toString(),
            record.referenceYearTotal.toFixed(2).toString(),
            `"${record.note ?? ""}"`,
            isNaN(variancePct) ? "n/a" : (variancePct * 100).toFixed(2).toString(),
            variance$.toFixed(2).toString(),
        ];

        tabularData.push(row.join(","));
    }
    const formattedData = tabularData.join("\n");
    return formattedData;
}

export function formatForAppfolio(
    {tab, data, config}:
        {
            tab: BudgetingType;
            data: ExportPropertyBudgetQuery;
            config: AppConfig;
        }): string {

    const $15EmptyCells = ",,,,,,,,,,,,,,";
    const $14EmptyCells = ",,,,,,,,,,,,,";
    const $13EmptyCells = ",,,,,,,,,,,,";
    const property = config.properties.currentProperty;
    const months = getMonths(property);
    const year = getYear(property, config, tab);
    const tabularData = [
        `"Budget for ${property?.name} - ${property?.address}"${$14EmptyCells}`,
        $15EmptyCells,
        `Fiscal Year,${year}${$13EmptyCells}`,
        $15EmptyCells,
        `Description,${$14EmptyCells}`,
        $15EmptyCells,
        [
            "Account Number",
            "Account Name",
            ...months,
            "Notes"
        ].join(",")
    ];

    const accountsOnly = (data.getPropertyBudgetExportSingleYear ?? []).filter(row => row.type == FinancialEntityType.Account);

    const accountsGroupedByClassification = accountsOnly.groupBy(row => row.accountClassification ?? "");

    for (const [classification, accountsGroup] of Object.entries(accountsGroupedByClassification)) {
        if (classification != null || classification != undefined) {
            tabularData.push($15EmptyCells);
            tabularData.push(classification + $14EmptyCells);
        }
        const accountsSorted = accountsGroup.sortBy("glNumber");
        for (const record of accountsSorted ?? []) {
            tabularData.push([
                `"${record.glNumber}"`,
                `"${record.glName}"`,
                ...record.yearValues.map(val => val.toFixed(2).toString()),
                `"${record.note ?? ""}"`,
            ].join(","));
        }
    }
    const formattedData = tabularData.join("\n");
    return formattedData;
}

export function formatForYardi(
    {tab, data, config, oneCentInsteadZero}:
        {
            tab: BudgetingType;
            data: ExportPropertyBudgetQuery;
            config: AppConfig;
            oneCentInsteadZero: boolean;
        }): string {

    const bookNum = config.chartOfAccountsConfig.rawData?.listChartOfAccounts?.yardiChartOfAccountsMetadata?.bookNum ?? "unknown";
    const $28EmptyCells = ",,,,,,,,,,,,,,,,,,,,,,,,,,,";
    const $12EmptyCells = ",,,,,,,,,,,";
    const property = config.properties.currentProperty;
    const year = getYear(property, config, tab);
    const monthIndices = getMonthIndexes(property);
    const tabularData = [["FinBudgets",$28EmptyCells].join(",")];
    const tranNum = Date.now();
    tabularData.push([
        "TranNum",
        "Property",
        "BookNum",
        "Year",
        "Account",
        ...(new Array(12).fill(null).map((_,index) => `Segment${index + 1}`)),
        ...(new Array(12).fill(null).map((_,index) => `MtdBudget${index + 1}`))
    ].join(","));

    const accountsOnly = (data.getPropertyBudgetExportSingleYear ?? []).filter(row => row.type == FinancialEntityType.Account);

    if(monthIndices[0] === 0){
        for (const record of accountsOnly ?? []) {
            const yearValues = record.yearValues.copy<number>();

            if(yearValues.every(val => val == 0) && oneCentInsteadZero){
                yearValues[0] = 0.01;
            }
            tabularData.push([
                `${tranNum}`,
                `${property?.code}`,
                `${bookNum}`,
                `${year}`,
                `${record.yardiExternalAccountId ?? record.glNumber}`,
                $12EmptyCells,
                ...yearValues.map(val => val.toFixed(2).toString()),
            ].join(","));
        }
    } else {
        const zeroIndex = monthIndices.findIndex(index => index == 0);
        for (const record of accountsOnly ?? []) {
            const firstYear: number[] = new Array(zeroIndex).fill(0);
            const secondYear: number[] = new Array(12 - zeroIndex).fill(0);

            for(let i = 0; i < zeroIndex; i++){
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                firstYear[i] = record.yearValues[i]!;
            }
            for(let i = zeroIndex, j = 0; i < 12; i++, j++) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                secondYear[j] = record.yearValues[i]!;
            }

            if(oneCentInsteadZero) {
                if(firstYear.every(val => val == 0)) {
                    firstYear[0] = 0.01;
                }
                if(secondYear.every(val => val == 0)) {
                    secondYear[0] = 0.01;
                }
            }

            const firstYearPadded: string[] = new Array(12).fill("");
            const secondYearPadded: string[] = new Array(12).fill("");

            const monthIndex = monthIndices[0];
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            firstYearPadded.splice(monthIndex!, firstYear.length, ...firstYear.map(val => val.toFixed(2).toString()));
            secondYearPadded.splice(0, secondYear.length, ...secondYear.map(val => val.toFixed(2).toString()));

            tabularData.push([
                `${tranNum}`,
                `${property?.code}`,
                `${bookNum}`,
                `${year}`,
                `${record.yardiExternalAccountId ?? record.glNumber}`,
                $12EmptyCells,
                ...firstYearPadded
            ].join(","));

            tabularData.push([
                `${tranNum}`,
                `${property?.code}`,
                `${bookNum}`,
                `${year + 1}`,
                `${record.yardiExternalAccountId ?? record.glNumber}`,
                $12EmptyCells,
                ...secondYearPadded
            ].join(","));
        }
    }
    const formattedData = tabularData.join("\n");
    return formattedData;
}

// TODO: this needs to be adjusted for fiscal year start month depending on how we get data back from the backend
export function formatForArbitraryMonthRange(
    data: GetBudgetSeasonExportDataQuery,
    startMonth: number, // from 0 to 23
    months: number, // from 1 to 24 minus start month is inclusive
    property: Property,
    budgetYear: number,
): string {
    const endMonth = startMonth + months; // this is endMonth + 1 in 0-based indexing
    if(startMonth >= endMonth || startMonth < 0 || endMonth > 24) {
        return "";
    }
    // turn data.financialValuesPropertyBudgetSeason into a map of accountId to 24 array values
    // default values to 0
    const accountIdToValues = new Map<string, number[]>();
    for (const record of data.financialValuesPropertyBudgetSeason ?? []) {
        let values = accountIdToValues.get(record.accountId);
        if(!values) {
            values = new Array(24).fill(0);
            accountIdToValues.set(record.accountId, values);
        }
        let shift = 0;
        if(record.versionType == VersionType.Budget) {
            shift = 12;
        }
        for(let i = 0; i < 12; i++) {
            values[i + shift] = record.values[i] ?? 0;
        }
    }
    const monthNames = getMonths(property);
    const headers = [
        "GL Number",
        "GL Name",
    ];
    let year = budgetYear - 1;
    let monthOffset = 0;
    for(let i = startMonth; i < endMonth; i++) {
        if(i > 11 && year == budgetYear - 1) {
            year = budgetYear;
            monthOffset = 12;
        }
        headers.push(`${year}-${monthNames[i - monthOffset]}`);
    }
    const tabularData = [headers.join(",")];

    const {chartOfAccounts: chartOfAccountsTree} = buildChartOfAccountsData(data.listChartOfAccountsById.records, data.listChartOfAccountsById.budgetComponentMappings);
    const chartOfAccounts = buildChartOfAccountsFlatComponentsBottomLine(chartOfAccountsTree, true);

    for (const record of chartOfAccounts) {
        const outputRow = [
            `"${record.number ?? ""}"`,
            `"${record.name}"`,
        ];
        for(let i = startMonth; i < endMonth; i++) {
            outputRow.push(accountIdToValues.get(record.id)?.[i]?.toFixed(2).toString() ?? "0.00");
        }
        tabularData.push(outputRow.join(","));
    }
    return tabularData.join("\n");
}